"use client";

import type { Variants } from "motion/react";
import { type MotionValue, motion, useTransform } from "motion/react";
import { memo, type ReactNode } from "react";
import { DEFAULT_CHART_ENTER_TRANSITION } from "./animation";
import { chartCssVars } from "./chart-context";
import {
  type ChartProjection,
  ProjectedBox,
  segmentsPath,
} from "./projection";

export interface SeriesPointMarkerStyle {
  /** Fill color for the inner circle */
  fill?: string;
  /** Outer ring stroke color. Default: same as `fill` */
  stroke?: string;
  /** Outer ring stroke width in px. Default: 2. Set to 0 to disable. */
  strokeWidth?: number;
  /** Gap between the inner fill and outer ring in px. Default: 2 */
  ringGap?: number;
  /** Optional outer outline beyond the ring. Default: 0 */
  outlineWidth?: number;
  /** Outer outline color. Default: same as `stroke` */
  outlineColor?: string;
  /** Point radius in px. Default: 5 */
  radius?: number;
  /** Dim non-active points while hovering. Default: true */
  fadeOnHover?: boolean;
  /** Opacity for non-hovered points when `fadeOnHover` is true. Default: 0.5 */
  inactiveOpacity?: number;
  /**
   * Blur in px for non-hovered points when `fadeOnHover` is true.
   * Applied once on the dimmed layer (not per dot) for performance. Default: 2
   */
  inactiveBlur?: number;
  /** Initial blur in px during enter animation. Default: 2 */
  enterBlur?: number;
  /** Enlarge the active point while hovering. Default: true */
  showActiveHighlight?: boolean;
}

interface MarkerCirclesProps {
  fill?: string;
  stroke?: string;
  strokeWidth: number;
  ringGap: number;
  outlineWidth: number;
  outlineColor?: string;
  radius: number;
}

function MarkerCircles({
  fill,
  stroke,
  strokeWidth,
  ringGap,
  outlineWidth,
  outlineColor,
  radius,
}: MarkerCirclesProps) {
  const resolvedStroke = stroke ?? fill ?? "currentColor";
  const resolvedOutlineColor = outlineColor ?? resolvedStroke;
  const ringOuter = strokeWidth > 0 ? radius + ringGap + strokeWidth : radius;
  const outlineRadius = outlineWidth > 0 ? ringOuter + outlineWidth / 2 : 0;

  return (
    <>
      {outlineWidth > 0 ? (
        <circle
          cx={0}
          cy={0}
          fill="none"
          r={outlineRadius}
          stroke={resolvedOutlineColor}
          strokeWidth={outlineWidth}
        />
      ) : null}
      <circle cx={0} cy={0} fill={fill} r={radius} />
      {strokeWidth > 0 ? (
        <circle
          cx={0}
          cy={0}
          fill="none"
          r={radius + ringGap + strokeWidth / 2}
          stroke={resolvedStroke}
          strokeWidth={strokeWidth}
        />
      ) : null}
    </>
  );
}

export interface StaticSeriesPointMarkerProps extends SeriesPointMarkerStyle {
  cx: number;
  cy: number;
  scale?: number;
}

export const StaticSeriesPointMarker = memo(function StaticSeriesPointMarker({
  cx,
  cy,
  scale = 1,
  fill,
  stroke,
  strokeWidth = 2,
  ringGap = 2,
  outlineWidth = 0,
  outlineColor,
  radius = 5,
}: StaticSeriesPointMarkerProps) {
  return (
    <g transform={`translate(${cx}, ${cy}) scale(${scale})`}>
      <MarkerCircles
        fill={fill}
        outlineColor={outlineColor}
        outlineWidth={outlineWidth}
        radius={radius}
        ringGap={ringGap}
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
    </g>
  );
});

export interface SeriesPointMarkerProps extends SeriesPointMarkerStyle {
  dataKey: string;
  index: number;
  cx: number;
  cy: number;
  revealDelay: number;
  revealEpoch: number;
  enterDuration: number;
}

/** Motion enter marker — used only while the chart reveal is running. */
export function SeriesPointMarker({
  dataKey,
  index,
  cx,
  cy,
  enterBlur = 2,
  revealDelay,
  revealEpoch,
  enterDuration,
  fill,
  stroke,
  strokeWidth = 2,
  ringGap = 2,
  outlineWidth = 0,
  outlineColor,
  radius = 5,
}: SeriesPointMarkerProps) {
  const variants: Variants = {
    hidden: {
      opacity: 0,
      filter: `blur(${enterBlur}px)`,
      scale: 1,
    },
    visible: {
      opacity: 1,
      filter: "blur(0px)",
      scale: 1,
      transition: {
        delay: revealDelay,
        duration: enterDuration,
        ease: DEFAULT_CHART_ENTER_TRANSITION.ease,
      },
    },
  };

  return (
    <g transform={`translate(${cx}, ${cy})`}>
      <motion.g
        animate="visible"
        initial="hidden"
        key={`${dataKey}-${index}-${revealEpoch}`}
        variants={variants}
      >
        <MarkerCircles
          fill={fill}
          outlineColor={outlineColor}
          outlineWidth={outlineWidth}
          radius={radius}
          ringGap={ringGap}
          stroke={stroke}
          strokeWidth={strokeWidth}
        />
      </motion.g>
    </g>
  );
}

export interface ProjectedSeriesPointMarkerProps extends SeriesPointMarkerStyle {
  projection: ChartProjection;
  dataKey: string;
  index: number;
  x: number;
  y: number;
  z: number;
  scale?: number;
  opacity?: number;
  revealing: boolean;
  revealDelay: number;
  revealEpoch: number;
  enterDuration: number;
  interactive: boolean;
  pillar?: number;
  highlighted?: boolean;
  onPointerEnter?: (event: React.PointerEvent<SVGGElement>) => void;
  onPointerLeave?: (event: React.PointerEvent<SVGGElement>) => void;
  onTouchStart?: (event: React.TouchEvent<SVGGElement>) => void;
  onClick?: (event: React.MouseEvent<SVGGElement>) => void;
}

const SHADOW_OPACITY = 0.18;
const STEM_OPACITY = 0.55;
const PILLAR_MORPH_END = 0.6;

function RevealGroup({
  revealing,
  enterBlur,
  revealDelay,
  enterDuration,
  revealKey,
  children,
}: {
  revealing: boolean;
  enterBlur: number;
  revealDelay: number;
  enterDuration: number;
  revealKey: string;
  children: ReactNode;
}) {
  if (!revealing) {
    return <g>{children}</g>;
  }
  const variants: Variants = {
    hidden: { opacity: 0, filter: `blur(${enterBlur}px)` },
    visible: {
      opacity: 1,
      filter: "blur(0px)",
      transition: {
        delay: revealDelay,
        duration: enterDuration,
        ease: DEFAULT_CHART_ENTER_TRANSITION.ease,
      },
    },
  };
  return (
    <motion.g
      animate="visible"
      initial="hidden"
      key={revealKey}
      variants={variants}
    >
      {children}
    </motion.g>
  );
}

function ProjectedMarkerCircles({
  cx,
  cy,
  scale,
  fill,
  stroke,
  strokeWidth,
  ringGap,
  outlineWidth,
  outlineColor,
  radius,
}: MarkerCirclesProps & {
  cx: MotionValue<number>;
  cy: MotionValue<number>;
  scale: number;
}) {
  const resolvedStroke = stroke ?? fill ?? "currentColor";
  const resolvedOutlineColor = outlineColor ?? resolvedStroke;
  const r = radius * scale;
  const gap = ringGap * scale;
  const ring = strokeWidth * scale;
  const outline = outlineWidth * scale;
  const ringOuter = ring > 0 ? r + gap + ring : r;

  return (
    <>
      <motion.circle cx={cx} cy={cy} fill="transparent" r={ringOuter + 2} />
      {outline > 0 ? (
        <motion.circle
          cx={cx}
          cy={cy}
          fill="none"
          r={ringOuter + outline / 2}
          stroke={resolvedOutlineColor}
          strokeWidth={outline}
        />
      ) : null}
      <motion.circle cx={cx} cy={cy} fill={fill} r={r} />
      {ring > 0 ? (
        <motion.circle
          cx={cx}
          cy={cy}
          fill="none"
          r={r + gap + ring / 2}
          stroke={resolvedStroke}
          strokeWidth={ring}
        />
      ) : null}
    </>
  );
}

export function ProjectedSeriesPointMarker({
  projection,
  dataKey,
  index,
  x,
  y,
  z,
  scale = 1,
  opacity = 1,
  revealing,
  revealDelay,
  revealEpoch,
  enterDuration,
  enterBlur = 2,
  interactive,
  pillar,
  highlighted = false,
  onPointerEnter,
  onPointerLeave,
  onTouchStart,
  onClick,
  fill,
  stroke,
  strokeWidth = 2,
  ringGap = 2,
  outlineWidth = 0,
  outlineColor,
  radius = 5,
}: ProjectedSeriesPointMarkerProps) {
  const { e } = projection;
  const headX = useTransform(e, (v) => projection.project(x, y, z, v)[0]);
  const headY = useTransform(e, (v) => projection.project(x, y, z, v)[1]);
  const footX = useTransform(e, (v) => projection.project(x, y, 0, v)[0]);
  const footY = useTransform(e, (v) => projection.project(x, y, 0, v)[1]);
  const shadowRadius = radius + ringGap;
  const shadowRy = useTransform(
    e,
    (v) => shadowRadius * Math.max(0.05, projection.at(v).cam.se)
  );
  const stem = useTransform(e, (v) =>
    segmentsPath([
      [projection.project(x, y, 0, v), projection.project(x, y, z, v)],
    ])
  );
  const shadowOpacity = useTransform(e, [0, 1], [0, SHADOW_OPACITY]);
  const stemOpacity = useTransform(e, [0, 1], [0, STEM_OPACITY]);
  const stemColor = stroke ?? fill ?? chartCssVars.foreground;
  const pillarOpacity = useTransform(e, [0, PILLAR_MORPH_END], [0, 1]);
  const headOpacity = useTransform(e, [0, PILLAR_MORPH_END], [1, 0]);
  const half = (pillar ?? 0) / 2;

  return (
    <g
      opacity={opacity}
      style={{ transition: "opacity 0.15s ease-in-out" }}
    >
      <RevealGroup
        enterBlur={enterBlur}
        enterDuration={enterDuration}
        revealDelay={revealDelay}
        revealing={revealing}
        revealKey={`${dataKey}-${index}-${revealEpoch}`}
      >
        {pillar === undefined ? (
          <g pointerEvents="none">
            <motion.ellipse
              cx={footX}
              cy={footY}
              fill={chartCssVars.foreground}
              rx={shadowRadius}
              ry={shadowRy}
              style={{ opacity: shadowOpacity }}
            />
            <motion.path
              d={stem}
              fill="none"
              stroke={stemColor}
              strokeWidth={1}
              style={{ opacity: stemOpacity }}
              vectorEffect="non-scaling-stroke"
            />
          </g>
        ) : null}
        <g
          data-point-index={index}
          onClick={onClick}
          onPointerEnter={onPointerEnter}
          onPointerLeave={onPointerLeave}
          onTouchStart={onTouchStart}
          style={{ cursor: interactive ? "pointer" : undefined }}
        >
          {pillar !== undefined && fill ? (
            <ProjectedBox
              box={{
                x0: x - half,
                x1: x + half,
                y0: y - half,
                y1: y + half,
                z0: 0,
                z1: Math.max(z, 1),
              }}
              fill={fill}
              hovered={highlighted}
              style={{ opacity: pillarOpacity }}
            />
          ) : null}
          <motion.g
            style={pillar === undefined ? undefined : { opacity: headOpacity }}
          >
            <ProjectedMarkerCircles
              cx={headX}
              cy={headY}
              fill={fill}
              outlineColor={outlineColor}
              outlineWidth={outlineWidth}
              radius={radius}
              ringGap={ringGap}
              scale={scale}
              stroke={stroke}
              strokeWidth={strokeWidth}
            />
          </motion.g>
        </g>
      </RevealGroup>
    </g>
  );
}

export function getSeriesMarkerVisualExtent(
  style: Pick<
    SeriesPointMarkerStyle,
    | "radius"
    | "strokeWidth"
    | "ringGap"
    | "outlineWidth"
    | "showActiveHighlight"
  >
): number {
  const radius = style.radius ?? 5;
  const strokeWidth = style.strokeWidth ?? 2;
  const ringGap = style.ringGap ?? 2;
  const outlineWidth = style.outlineWidth ?? 0;
  const showActiveHighlight = style.showActiveHighlight ?? true;
  const ring = strokeWidth > 0 ? ringGap + strokeWidth : 0;
  const outline = outlineWidth > 0 ? outlineWidth : 0;
  const highlightPad = showActiveHighlight ? radius * 0.35 : 0;
  return radius + ring + outline + highlightPad + 2;
}
